/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  Action,
  ActionIcon,
  Card, CardProps, CardSkeleton, Dropdown, FontWeight, HStack, ICON_PLUS, ICON_SYNC, Message, ModalSize, Portal, Spacing, Stack, Text, TextSkeleton, useModal,
} from '@doveit/bricks';
import { uniqueId } from 'lodash';
import { pluralizeWord } from '@doveit/hammer';
import { BrandTemplateAutofillDecodedDataset, TemplatePropertiesSoldSelectorSlotsConfig } from '../../utils/autofill/types';
import useProperties from '../../../property/hooks/use-properties/useProperties';
import { useAgent } from '../../../hooks/use-agent/useAgent';
import { removeNullAndUndefinedValues } from '../../../utils/array';
import { Aggregator, Status } from '../../../domain/types';
import useSearchProperties from '../../../property/hooks/use-search-properties/useSearchProperties';
import { useSlotSelector } from '../../../hooks/use-slot-selector/useSlotSelector';
import { toBrandTemplateAutofillPropertyData } from '../../utils/autofill/utils';
import { Content, PropertyPreview } from '../../../providers/api/dtos';
import PropertySelectablePreview from '../../../property/components/property-selectable-preview/PropertySelectablePreview';
import PropertyAvatar from '../../../property/components/property-avatar/PropertyAvatar';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import * as styles from './TemplatePropertiesSoldSelector.style';
import SearchByQueryAction from '../../../components/search-by-query-action/SearchByQueryAction';
import ScrollableArea from '../../../components/scrollable-area/ScrollableArea';
import { useStatefulPagination } from '../../../hooks/use-pagination/usePagination';
import PaginatedList from '../../../components/paginated-list/PaginatedList';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import useScroogeProperties from '../../../property/hooks/use-scrooge-properties/useScroogeProperties';
import { getFieldSlots } from '../../utils/autofill/autofill';
import PropertyPhotosSelector from '../../../property/containers/property-photos-selector/PropertyPhotosSelector';
import ViewEntityPreviewSkeleton from '../../../components/view-entity-preview-skeleton/ViewEntityPreviewSkeleton';

export interface TemplatePropertiesSoldSelectorProps extends React.AriaAttributes {
  agentEmail?: string,
  totalSlots: number,
  disabled?: boolean,
  asContent?: CardProps['asContent'],
  decodedDataset: BrandTemplateAutofillDecodedDataset,
  onChange?: (slots: TemplatePropertiesSoldSelectorSlotsConfig) => void,
}

interface ModalData {
  slotIndex: number,
  property?: PropertyPreview,
}

const TemplatePropertiesSoldSelector: React.FC<TemplatePropertiesSoldSelectorProps> = ({
  agentEmail,
  totalSlots,
  disabled = false,
  decodedDataset,
  asContent,
  onChange,
}) => {
  const isMobile = useIsDevice('mobile');
  const modal = useModal<ModalData>();

  const { data: agent } = useAgent(agentEmail);

  const scrollRef = React.useRef<HTMLDivElement>(null);
  const { page, goToPage } = useStatefulPagination(scrollRef);
  const [searchQuery, setSearchQuery] = React.useState<string>('');

  const [propertiesSlot, setPropertiesSlot] = React.useState<Map<number, PropertyPreview>>(new Map());
  const [photosSlot, setPhotosSlot] = React.useState<Map<number, Map<number, Content>>>(new Map());

  const agentEmailsFilter = React.useMemo(() => {
    if (!agent) return undefined;

    if (agent.canManage && agent.canManage.length > 0) {
      return removeNullAndUndefinedValues([agent.email.work, ...agent.canManage.map(({ email }) => email.work)]);
    }

    return [agent.email.work!];
  }, [agent]);

  const {
    data: preSelectedProperties,
    isLoading: arePreSelectedPropertiesLoading,
    error: preSelectedPropertiesError,
  } = useProperties({
    agentEmail: agentEmailsFilter,
    status: Status.VENDUTO,
    publishedOn: Aggregator.DOVE_IT,
  }, { size: totalSlots });

  const {
    data: allProperties,
    isLoading: areAllPropertiesLoading,
    error: allPropertiesError,
  } = useSearchProperties(searchQuery, {
    agentEmail: agentEmailsFilter,
    status: Status.VENDUTO,
    publishedOn: Aggregator.DOVE_IT,
  }, {
    page,
    size: 10,
  });

  const selectedPropertyIds = React.useMemo(
    () => removeNullAndUndefinedValues([...propertiesSlot.values()].map((c) => c.id)),
    [propertiesSlot],
  );

  const {
    data: scroogeProperties,
    isLoading: areScroogePropertiesLoading,
  } = useScroogeProperties({
    id: selectedPropertyIds,
  }, {
    size: selectedPropertyIds.length,
  });

  const scroogePropertiesById = React.useMemo(() => new Map(
    (scroogeProperties?.content ?? []).map((property) => [property.id, property]),
  ), [scroogeProperties]);

  const initialItems = React.useMemo(
    () => preSelectedProperties?.content,
    [preSelectedProperties],
  );

  const { assignSlot, deselectSlot } = useSlotSelector<PropertyPreview>({
    totalSlots,
    initialItems,
    onChange: setPropertiesSlot,
  });

  const enrichedPropertiesSlot = React.useMemo(() => {
    const result: TemplatePropertiesSoldSelectorSlotsConfig = new Map();

    propertiesSlot.forEach((property, slotIndex) => {
      const matchingScroogeProperty = property.id ? scroogePropertiesById.get(property.id) : undefined;
      const propertyData = toBrandTemplateAutofillPropertyData(property, matchingScroogeProperty);
      const photos = photosSlot.get(slotIndex);

      result.set(slotIndex, photos ? { ...propertyData, photos } : propertyData);
    });

    return result;
  }, [propertiesSlot, scroogePropertiesById, photosSlot]);

  React.useEffect(() => {
    onChange?.(enrichedPropertiesSlot);
  }, [onChange, enrichedPropertiesSlot]);

  const selectedPropertiesIds = React.useMemo(() => new Set([...propertiesSlot.values()].map((c) => c.id!)), [propertiesSlot]);

  const slotIndexByPropertyId = React.useMemo(() => {
    const map = new Map<number, number>();
    propertiesSlot.forEach((property, slotIndex) => {
      if (property.id) map.set(property.id, slotIndex);
    });
    return map;
  }, [propertiesSlot]);

  const openModal = React.useCallback((slotIndex: number) => () => {
    if (disabled) return;

    setSearchQuery('');

    modal.open({ slotIndex, property: propertiesSlot.get(slotIndex) });
  }, [disabled, modal, propertiesSlot]);

  const handlePhotosChange = React.useCallback((slotIndex: number) => (photos: Map<number, Content>) => {
    setPhotosSlot((prev) => {
      const next = new Map(prev);
      next.set(slotIndex, photos);
      return next;
    });
  }, []);

  const handlePropertySelect = React.useCallback((property: PropertyPreview) => {
    if (!modal.data) return;

    const isAlreadySelected = modal.data.property?.id === property.id;

    modal.setData({
      slotIndex: modal.data.slotIndex,
      property: isAlreadySelected ? undefined : property,
    });
  }, [modal]);

  const handleDeselectSlot = React.useCallback((slotIndex: number) => {
    deselectSlot(slotIndex);

    setPhotosSlot((prev) => {
      if (!prev.has(slotIndex)) return prev;
      const next = new Map(prev);
      next.delete(slotIndex);
      return next;
    });
  }, [deselectSlot]);

  const confirmSelection = React.useCallback(() => {
    if (typeof modal.data?.slotIndex === 'undefined' || !modal.data.property) return;

    const { slotIndex } = modal.data;

    assignSlot(slotIndex, modal.data.property);

    setPhotosSlot((prev) => {
      if (!prev.has(slotIndex)) return prev;
      const next = new Map(prev);
      next.delete(slotIndex);
      return next;
    });

    modal.close();
  }, [assignSlot, modal]);

  const slotKeys = React.useMemo(
    () => Array.from({ length: totalSlots }, () => uniqueId('properties-sold-slot-')),
    [totalSlots],
  );

  if (totalSlots === 0) return null;

  return (
    <>
      <Card
        aria-label="Sezione per la selezione degli immobili venduti"
        asContent={asContent}
      >
        <Card.Header title={`${pluralizeWord('Immobil', 'e', 'i', totalSlots)} ${pluralizeWord('vendut', 'o', 'i', totalSlots)}${totalSlots > 1 ? ` (${propertiesSlot.size} di ${totalSlots})` : ''}`} />

        {preSelectedPropertiesError && (
          <Card.Content>
            <Message
              type="critical"
              message="Non è stato possibile caricare gli immobili venduti."
            />
          </Card.Content>
        )}

        {preSelectedProperties && preSelectedProperties.content.length === 0 && (
          <Card.Content>
            <Message
              type="warning"
              message="Non sono presenti immobili venduti."
            />
          </Card.Content>
        )}

        {(arePreSelectedPropertiesLoading || areScroogePropertiesLoading) && Array.from({ length: totalSlots }).map((_, slotIndex) => (
          <React.Fragment key={slotKeys[slotIndex]}>
            <Card.Divider />
            <Card.Content aria-label="Immobile in caricamento">
              <CardSkeleton
                key={slotKeys[slotIndex]}
                paddingSize="XS"
                asContent="unpadded"
              >
                <TextSkeleton count={2} />
              </CardSkeleton>
            </Card.Content>
          </React.Fragment>
        ))}

        {preSelectedProperties && preSelectedProperties.content.length > 0 && Array.from({ length: totalSlots }).map((_, slotIndex) => {
          const assignedProperty = enrichedPropertiesSlot.get(slotIndex);

          const selectablePhotos = getFieldSlots(
            decodedDataset.variables,
            'agent_data',
            { field: 'propertiesSold', index: slotIndex + 1 },
          ).length;

          return (
            <React.Fragment key={slotKeys[slotIndex]}>
              <Card.Divider />

              <Card.Content
                highlighted={!assignedProperty}
              >
                <HStack
                  gap={150}
                  wrap="nowrap"
                  alignItems="flex-start"
                >
                  {totalSlots > 1 && (
                    <div css={styles.index}>
                      {slotIndex + 1}
                    </div>
                  )}

                  {!assignedProperty && (
                    <div css={styles.emptySlot}>
                      <HStack wrap="nowrap" justifyContent="space-between">
                        <HStack gap={100}>
                          <PropertyAvatar size="S" />

                          <Text.BodySmall fontWeight={FontWeight.MEDIUM}>
                            Non selezionato
                          </Text.BodySmall>
                        </HStack>

                        <ActionIcon
                          size="S"
                          label="Seleziona"
                          aria-label="Azione per selezionare un immobile"
                          icon={{ path: ICON_PLUS }}
                          disabled={disabled}
                          onClick={openModal(slotIndex)}
                          emphasis="high"
                        />
                      </HStack>
                    </div>
                  )}

                  {assignedProperty && (
                    <Stack gap={200} wrap="nowrap">
                      <PropertySelectablePreview
                        property={assignedProperty}
                        asContent="unpadded"
                        primaryActions={[
                          <ActionIcon
                            size="S"
                            label="Cambia"
                            aria-label="Azione per cambiare l'immobile selezionata"
                            icon={{ path: ICON_SYNC }}
                            disabled={disabled}
                            onClick={openModal(slotIndex)}
                          />,
                        ]}
                        secondaryActions={[
                          <Dropdown.Option
                            label="Deseleziona"
                            aria-label="Azione per deselezionare l'immobile"
                            disabled={disabled}
                            onClick={() => handleDeselectSlot(slotIndex)}
                          />,
                        ]}
                      />

                      {selectablePhotos > 0 && (
                        <>
                          <div css={styles.divider} />

                          <PropertyPhotosSelector
                            propertyId={assignedProperty.id!}
                            totalSlots={selectablePhotos}
                            onChange={handlePhotosChange(slotIndex)}
                            disabled={disabled}
                          />
                        </>
                      )}
                    </Stack>
                  )}
                </HStack>
              </Card.Content>
            </React.Fragment>
          );
        })}
      </Card>

      <Portal>
        {modal.data && (
          <SimpleModal
            {...modal}
            size={ModalSize.MEDIUM}
            title={`Seleziona immobile venduto${totalSlots > 1 ? ` #${modal.data.slotIndex + 1}` : ''}`}
            aria-label={`Seleziona immobile venduto per slot #${modal.data.slotIndex + 1}`}
            padded={false}
            footer={(
              <Action
                color="primary"
                label="Conferma"
                emphasis="high"
                disabled={!modal.data?.property}
                onClick={confirmSelection}
              />
            )}
          >
            <div css={styles.searchBar}>
              <Text.Body>
                Seleziona l&apos;immobile {totalSlots > 1 ? `#${modal.data.slotIndex + 1}` : ''} dalla lista di immobili venduti, cliccando sulla relativa anteprima.
              </Text.Body>

              <SearchByQueryAction
                initialQuery={searchQuery}
                onSearch={setSearchQuery}
                width="100%"
                placeholder="Cerca per rif. o indirizzo..."
                size="M"
                variant="input"
              />
            </div>

            <ScrollableArea height={isMobile ? '40rem' : '25rem'}>
              <Spacing padding={300}>
                {allPropertiesError && (
                  <Message
                    type="critical"
                    message="Non è stato possibile caricare gli immobili venduti."
                  />
                )}

                {areAllPropertiesLoading && (
                  <Stack gap={200} aria-label="Immobili in caricamento">
                    {Array.from({ length: 2 }).map((_, slotIndex) => (
                      <ViewEntityPreviewSkeleton key={slotKeys[slotIndex]} />
                    ))}
                  </Stack>
                )}

                {allProperties && (
                  <PaginatedList
                    {...allProperties}
                    emptyResultMessage="Nessun immobile venduto trovato."
                    goToPage={goToPage}
                    hideResultCount
                  >
                    {allProperties.content.map((property) => {
                      const isSelectedInThisModal = modal.data?.property?.id === property.id;
                      const isSelectedInAnotherSlot = selectedPropertiesIds.has(property.id!) && propertiesSlot.get(modal.data?.slotIndex ?? -1)?.id !== property.id;
                      const isClickable = !isSelectedInAnotherSlot;
                      const isSelected = isSelectedInThisModal || isSelectedInAnotherSlot;

                      let selectedIndex: number | undefined;

                      if (isSelectedInThisModal) {
                        selectedIndex = (modal.data?.slotIndex ?? 0) + 1;
                      } else if (isSelectedInAnotherSlot) {
                        selectedIndex = (slotIndexByPropertyId.get(property.id!) ?? 0) + 1;
                      }

                      return (
                        <PropertySelectablePreview
                          key={property.id}
                          property={property}
                          selected={isSelected}
                          disabled={isSelectedInAnotherSlot}
                          operation={isSelected ? 'remove' : 'add'}
                          onClick={isClickable ? () => handlePropertySelect(property) : undefined}
                          index={totalSlots > 1 ? selectedIndex : undefined}
                        />
                      );
                    })}
                  </PaginatedList>
                )}
              </Spacing>
            </ScrollableArea>
          </SimpleModal>
        )}
      </Portal>
    </>
  );
};

export default TemplatePropertiesSoldSelector;
